import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import i18next from 'i18next';
import { addDays, addWeeks, isValid, startOfDay, startOfWeek } from 'date-fns';
import { pl } from 'date-fns/locale';

import { InputWithOptions, OtoSpinner } from '../common';
import type { TDisableOrdersModalProps } from 'types';
import { REASONS_TO_CLOSE } from 'config';
import { formatDeliveryHours } from './DisableOrdersToggle';

interface IProps extends TDisableOrdersModalProps {
  closeModal: () => void;
}

export const getDisableUntilOptions = () => [
  {
    label: i18next.t('disable-orders-modal.until-end-of-day'),
    value: startOfDay(addDays(new Date(), 1)).toISOString(),
  },
  { label: i18next.t('disable-orders-modal.until-revoked'), value: '' },
  {
    label: i18next.t('disable-orders-modal.until-next-week'),
    value: startOfWeek(addWeeks(new Date(), 1), { locale: pl }).toISOString(),
  },
];

const DisableOrdersModal: React.FC<IProps> = (props: IProps) => {
  const { closeModal, onValuesSelected, todayDeliveryHours } = props;

  const reasonOptions = useMemo(
    () => [
      { label: '', value: '' },
      ...REASONS_TO_CLOSE.map((reason) => ({
        ...reason,
        label: i18next.t(reason.label),
      })),
    ],
    []
  );

  const disableUntilOptions = useMemo(() => getDisableUntilOptions(), []);

  const [reason, setReason] = useState(reasonOptions[0].value);
  const [disableUntil, setDisableUntil] = useState(
    disableUntilOptions[0].value
  );
  const isLoading = false;

  const disableUntilDate = new Date(disableUntil);

  return (
    <>
      <Modal isOpen>
        <ModalHeader toggle={closeModal}>
          {i18next.t('disable-orders-toggle.disabled-confirmation')}
        </ModalHeader>

        <>
          <ModalBody>
            <p>
              <span className="text-danger">
                {i18next.t('disable-orders-modal.no-need-to-disable-daily')}
              </span>{' '}
              {i18next.t('disable-orders-modal.auto-block-hint', { hours: formatDeliveryHours(todayDeliveryHours) })}
            </p>
            <p>
              {i18next.t('disable-orders-modal.use-when')}
            </p>
            <label className="font-weight-bold mt-2">{i18next.t('disable-orders-modal.choose-reason')}</label>
            <InputWithOptions
              allowEmptyOnChange={false}
              value={reason}
              options={reasonOptions}
              onChange={(e) => setReason(e.target.value)}
            />
            <label className="font-weight-bold mt-4">
              {i18next.t('disable-orders-modal.until-when')}
            </label>
            <Input
              type="select"
              value={disableUntil}
              onChange={(e) => setDisableUntil(e.target.value)}
            >
              {disableUntilOptions.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
            <p className="mt-3">
              <strong className="d-block">
                {i18next.t('disable-orders-modal.block-other-day')}
              </strong>{' '}
              <div>
                {i18next.t('disable-orders-modal.block-other-day-hint')}{' '}
                <Link onClick={closeModal} to="/settings/special-days">
                  {i18next.t('nav.Ustawienia')} &gt;{' '}
                  {i18next.t('nav.Dni wyjątkowe')}
                </Link>
                .
              </div>
            </p>
          </ModalBody>
          <ModalFooter>
            {isLoading ? (
              <OtoSpinner />
            ) : (
              <>
                <Button type="button" color="secondary" onClick={closeModal}>
                  {i18next.t('disable-orders-modal.close')}
                </Button>
                <Button
                  type="button"
                  color="success"
                  onClick={() =>
                    onValuesSelected({
                      reasonToDisable: reason,
                      disableUntil: disableUntil,
                    }).finally(() => closeModal())
                  }
                  disabled={
                    !reason || (!!disableUntil && !isValid(disableUntilDate))
                  }
                >
                  {i18next.t('disable-orders-toggle.turn-off')}
                </Button>
              </>
            )}
          </ModalFooter>
        </>
      </Modal>
    </>
  );
};

DisableOrdersModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default DisableOrdersModal;
